import React, { useEffect, useState, useMemo } from "react";
import {
  Building2, Users, MessageCircle, TrendingUp, Activity, Sparkles,
  ShieldCheck, AlertOctagon, Search,
} from "lucide-react";
import { endpoints } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

/**
 * Super Admin Dashboard (Fitnudge internal). Gated by SUPER_ADMIN_EMAILS env.
 */
export default function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const [overview, setOverview] = useState(null);
  const [gyms, setGyms] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user?.is_super_admin) {
      setLoading(false);
      return;
    }
    Promise.all([endpoints.adminOverview(), endpoints.adminGyms()])
      .then(([o, g]) => { setOverview(o.data); setGyms(g.data || []); })
      .catch(() => toast.error("Could not load admin data"))
      .finally(() => setLoading(false));
  }, [authLoading, user]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return gyms;
    return gyms.filter(g => `${g.name} ${g.owner_email} ${g.owner_name}`.toLowerCase().includes(ql));
  }, [gyms, q]);

  if (authLoading || loading) {
    return <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">Loading…</div>;
  }

  if (!user?.is_super_admin) {
    return (
      <div className="max-w-md mx-auto mt-20 text-center" data-testid="admin-no-access">
        <div className="w-12 h-12 rounded-full bg-rose-100 dark:bg-rose-950/30 flex items-center justify-center mx-auto mb-4">
          <AlertOctagon className="w-5 h-5 text-rose-600" />
        </div>
        <h1 className="font-display text-xl font-bold text-foreground mb-1">Super admin only</h1>
        <p className="text-sm text-muted-foreground">This page is restricted to the Fitnudge team.</p>
      </div>
    );
  }

  const tiles = [
    { key: "total_gyms",            label: "Total gyms",         icon: Building2,    fmt: v => v },
    { key: "active_gyms_7d",        label: "Active (7d)",        icon: Activity,     fmt: v => v },
    { key: "daily_active_gyms",     label: "Daily active",       icon: Sparkles,     fmt: v => v },
    { key: "active_members_managed",label: "Members managed",    icon: Users,        fmt: v => v },
    { key: "leads_this_month",      label: "Leads this month",   icon: TrendingUp,   fmt: v => v },
    { key: "messages_sent_30d",     label: "Messages (30d)",     icon: MessageCircle, fmt: v => v },
  ];

  return (
    <div className="px-4 lg:px-8 py-6 lg:py-8 max-w-7xl mx-auto" data-testid="admin-dashboard">
      <header className="mb-6">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-indigo-500 mb-1">Fitnudge Admin</p>
        <h1 className="font-display text-3xl font-bold tracking-tight text-foreground">Internal overview</h1>
        <p className="text-sm text-muted-foreground mt-1">Health, usage, and activity across every gym workspace.</p>
      </header>

      {/* Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8" data-testid="admin-tiles">
        {tiles.map(t => {
          const Icon = t.icon;
          return (
            <div key={t.key} data-testid={`admin-tile-${t.key}`} className="border border-border rounded-xl p-4 bg-card">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">{t.label}</p>
                <Icon className="w-3.5 h-3.5 text-indigo-500" />
              </div>
              <div className="font-display text-2xl font-bold tabular-nums">{t.fmt(overview?.[t.key] ?? 0)}</div>
            </div>
          );
        })}
      </div>

      {/* Gym list */}
      <section data-testid="admin-gyms">
        <div className="flex items-center justify-between mb-3 gap-3">
          <h2 className="font-display text-lg font-semibold text-foreground">Gyms ({filtered.length})</h2>
          <div className="relative w-full max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              data-testid="admin-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, email, owner…"
              className="pl-8 h-9 text-sm"
            />
          </div>
        </div>

        <div className="border border-border rounded-xl bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 border-b border-border">
                <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground/80">
                  <th className="px-3 py-2 font-semibold">Gym</th>
                  <th className="px-3 py-2 font-semibold">Owner</th>
                  <th className="px-3 py-2 font-semibold text-right">Members</th>
                  <th className="px-3 py-2 font-semibold text-right">Leads</th>
                  <th className="px-3 py-2 font-semibold">Last active</th>
                  <th className="px-3 py-2 font-semibold">Health</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.length === 0 ? (
                  <tr><td colSpan="6" className="px-3 py-12 text-center text-muted-foreground">No gyms match.</td></tr>
                ) : filtered.map(g => (
                  <tr key={g.id} data-testid={`admin-gym-row-${g.id}`} className="hover:bg-muted/20 transition-colors">
                    <td className="px-3 py-3">
                      <div className="font-medium text-foreground">{g.name}</div>
                      <div className="text-[10px] text-muted-foreground/70 font-mono">{g.id}</div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="text-foreground">{g.owner_name || "-"}</div>
                      <div className="text-[11px] text-muted-foreground/80">{g.owner_email || "-"}</div>
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums font-medium">{g.member_count}</td>
                    <td className="px-3 py-3 text-right tabular-nums font-medium">{g.lead_count}</td>
                    <td className="px-3 py-3 text-xs text-muted-foreground">
                      {g.last_active ? new Date(g.last_active).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "-"}
                    </td>
                    <td className="px-3 py-3">
                      <HealthCell score={g.health_score} label={g.health_label} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}

function HealthCell({ score, label }) {
  const cls = score >= 80
    ? "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300"
    : score >= 60
    ? "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300"
    : score >= 40
    ? "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300"
    : "bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-950/30 dark:text-rose-300";
  return (
    <div className="flex items-center gap-2">
      <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-md border ${cls}`}>
        {label}
      </span>
      <span className="text-[11px] tabular-nums text-muted-foreground">{score}</span>
    </div>
  );
}
